import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./SecondaryTopbar.css";
import { UserContext } from "../../../../../App";
import useGetDevice from "../../../product/hooks/useGetDevice";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";

const MergeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-git-merge mr-1.5 h-3.5 w-3.5"
    aria-hidden="true"
  >
    <circle cx="18" cy="18" r="3" />
    <circle cx="6" cy="6" r="3" />
    <path d="M6 21V9a9 9 0 0 0 9 9" />
  </svg>
);

const SplitIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-split mr-1.5 h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M16 3h5v5" />
    <path d="M8 3H3v5" />
    <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
    <path d="m15 9 6-6" />
  </svg>
);

const GridIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SecondaryTopbar = ({
  activeChip,
  setActiveChip,
  onClickFunction,
  floorPlan,
  selectedFloor,
  onFloorChange,
  loader,
  allTables = [],
}) => {
  const { saleMode, setSaleMode } = useContext(UserContext);
  const isMobileView = useGetDevice();
  const showSnackbar = useSnackbar();
  const [showFloorTabs, setShowFloorTabs] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const floorTabsRef = useRef(null);
  const floorRowRef = useRef(null);
  const floorToggleBtnRef = useRef(null);

  const counts = useMemo(() => {
    const all = allTables.length;
    const available = allTables.filter((t) => t?.status === "AVAILABLE").length;
    const occupied = allTables.filter(
      (t) =>
        t?.status === "OCCUPIED" &&
        t?.cartStatus !== "ORDER_CREATED" &&
        t?.cartStatus !== "PAYMENT_PENDING"
    ).length;
    const payment = allTables.filter(
      (t) => t?.cartStatus === "ORDER_CREATED" || t?.cartStatus === "PAYMENT_PENDING"
    ).length;
    return { all, available, occupied, payment };
  }, [allTables]);

  const checkScrollability = useCallback(() => {
    const el = floorTabsRef.current;
    if (!el) {
      setCanScrollRight(false);
      return;
    }
    setCanScrollRight(
      el.scrollWidth > el.clientWidth + 2 && el.scrollLeft < el.scrollWidth - el.clientWidth - 2
    );
  }, []);

  useEffect(() => {
    const handler = () => setShowFloorTabs((prev) => !prev);
    window.addEventListener("tableLayout:toggleFloorBar", handler);
    return () => window.removeEventListener("tableLayout:toggleFloorBar", handler);
  }, []);

  useEffect(() => {
    if (!showFloorTabs) return;
    const handleOutsideClick = (e) => {
      if (floorRowRef.current?.contains(e.target) || floorToggleBtnRef.current?.contains(e.target))
        return;
      setShowFloorTabs(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showFloorTabs]);

  useEffect(() => {
    if (!showFloorTabs) {
      setCanScrollRight(false);
      return;
    }
    const timer = setTimeout(checkScrollability, 30);
    const el = floorTabsRef.current;
    if (el) {
      el.addEventListener("scroll", checkScrollability);
      const ro = new ResizeObserver(checkScrollability);
      ro.observe(el);
      return () => {
        clearTimeout(timer);
        el.removeEventListener("scroll", checkScrollability);
        ro.disconnect();
      };
    }
    return () => clearTimeout(timer);
  }, [showFloorTabs, checkScrollability, floorPlan]);

  const handleChipClick = (key) => setActiveChip(activeChip === key ? null : key);

  const handleSaleModeChange = (mode) => {
    if (!mode) return;
    setSaleMode(mode);
    if (mode === "TAKE_AWAY" || mode === "DELIVERY") {
      localStorage.setItem("fineDineSaleMode", mode);
      onClickFunction(mode);
    } else {
      localStorage.removeItem("fineDineSaleMode");
    }
  };

  const handleFloorClick = (floor) => {
    if (!floor) return;
    onFloorChange({
      label: floor.label,
      value: floor.value,
      parentLabel: floor.label,
      parentValue: floor.value,
    });
    setShowFloorTabs(false);
  };

  const handleComingSoon = () => showSnackbar("Feature coming soon", "info");

  return (
    <div className="table-secondary-topbar-wrap">
      <div className="table-secondary-topbar">
        {/* ── Left: filter chips ── */}
        <div className="stb-left">
          {/* All pill */}
          <button
            className={`stb-all-pill${!activeChip ? " active" : ""}`}
            onClick={() => setActiveChip(null)}
          >
            <span className="stb-all-avatar">A</span>
            All ({counts.all})
          </button>

          {/* Available */}
          <button
            className={`stb-filter-item stb-filter-item--available${
              activeChip === "AVAILABLE" ? " active" : ""
            }`}
            onClick={() => handleChipClick("AVAILABLE")}
          >
            <span className="stb-dot stb-dot--available" />
            Available ({counts.available})
          </button>

          {/* Occupied */}
          <button
            className={`stb-filter-item stb-filter-item--occupied${
              activeChip === "OCCUPIED" ? " active" : ""
            }`}
            onClick={() => handleChipClick("OCCUPIED")}
          >
            <span className="stb-dot stb-dot--occupied" />
            Occupied ({counts.occupied})
          </button>

          {/* Payment */}
          <button
            className={`stb-filter-item stb-filter-item--payment${
              activeChip === "ORDER_CREATED" ? " active" : ""
            }`}
            onClick={() => handleChipClick("ORDER_CREATED")}
          >
            <span className="stb-dot stb-dot--payment" />
            Payment ({counts.payment})
          </button>
        </div>

        {/* ── Middle: Merge / Split / Floor ── */}

        {/* ── Right: sale modes ── */}
        <div className="stb-last-length">
          <div className="stb-middle">
            {!isMobileView && (
              <div className="stb-action-group">
                <button
                  className="stb-action-btn"
                  type="button"
                  data-testid="button-open-merge-table-dialog"
                  onClick={handleComingSoon}
                >
                  <MergeIcon /> Merge
                </button>
                <button
                  className="stb-action-btn"
                  type="button"
                  data-testid="button-open-split-table-dialog"
                  onClick={handleComingSoon}
                >
                  <SplitIcon /> Split
                </button>
              </div>
            )}

            <button
              ref={floorToggleBtnRef}
              className={`stb-floor-btn${showFloorTabs ? " active" : ""}`}
              onClick={() => setShowFloorTabs((p) => !p)}
            >
              <GridIcon />
              Floor
              <ChevronDown />
            </button>
          </div>

          <div className="stb-right">
            {[
              { key: "DINE_IN", label: "Dine In" },
              { key: "TAKE_AWAY", label: "Takeaway" },
              { key: "DELIVERY", label: "Delivery" },
              { key: "RESERVE", label: "Reserve" },
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`stb-mode-btn${saleMode === key ? " active" : ""}`}
                onClick={() => {
                  if (key === "RESERVE") {
                    handleComingSoon();
                  } else {
                    handleSaleModeChange(key);
                  }
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Floor dropdown */}
      {showFloorTabs && floorPlan.length > 0 && (
        <div ref={floorRowRef} className="secondary-topbar-floor-row">
          <div
            className={`secondary-topbar-floor-tabs-wrap${
              canScrollRight ? " can-scroll-right" : ""
            }`}
          >
            <div ref={floorTabsRef} className="secondary-topbar-floor-tabs">
              {floorPlan.map((floor) => (
                <button
                  key={floor.value}
                  type="button"
                  className={`secondary-topbar-floor-tab${
                    selectedFloor?.parentValue === floor.value ? " active" : ""
                  }`}
                  onClick={() => handleFloorClick(floor)}
                  disabled={loader}
                >
                  {floor.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(SecondaryTopbar);
